import { Box, Modal, useTheme, Button } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useFetchTable } from "@hooks/useFetchDataTable";
import Table from "@components/Table";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { useParams } from "react-router-dom";
import MailIcon from "@mui/icons-material/Mail";
import { ActionsType } from "src/interfaces/Common";
import Alert from "@mui/material/Alert";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

interface SessionInvitationModalCstInterface {
  open: boolean;
  setOpen: (open: boolean) => void;
  idMeeting: number | string | null;
  meeting: string | null;
  role?: boolean;
  onResendClick?: () => void;
}
const SessionInvitationModalCst: React.FC<
  SessionInvitationModalCstInterface
> = ({
  open,
  setOpen,
  idMeeting,
  meeting = null,
  role = true,
  onResendClick,
}) => {
  const { t } = useTranslation(["modal"]);
  const theme = useTheme();
  const { id } = useParams();

  const pageId = PagesID["corporate.virtual-session-invitations"];
  const { user } = useAuth();
  const { filters, setFilters } = useFilters();
  const filterId = `${user?.id_user}_${pageId}`;

  const { dataTable, columns, actionsTopToolbar, isLoading } = useFetchTable(
    open && idMeeting !== null
      ? role
        ? `${ServicesURL.invitationStatus.replace(":meetingId", String(idMeeting))}?corporateId=${id || ""}`
        : `${ServicesURL.invitationStatusSession.replace(":sessionId", String(idMeeting))}?corporateId=${id || ""}`
      : null,
    pageId,
    filters[filterId],
  );

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns
        .filter((el) => !(el.accessorKey === "role" && role === false))
        .map((el) => {
          if (el.accessorKey === "role") {
            return {
              ...el,
              optionsKey:
                meeting === "MSTEAMS"
                  ? "meeting_role_teams"
                  : "meeting_role_gmeet",
            };
          }
          return { ...el };
        });
    }
  }, [columns]);

  const handleResendNotifications = React.useCallback(() => {
    if (onResendClick) onResendClick();
  }, [onResendClick]);

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    if (!dataTable?.resend_enabled) return [];
    return actionsTopToolbar?.map((action: ActionsType) => {
      if (action.key === "mail") {
        return {
          ...action,
          getIcon: () => <MailIcon />,
          callBack: handleResendNotifications,
        };
      }
      return action;
    });
  }, [actionsTopToolbar, handleResendNotifications, dataTable]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setFilters(filterId, { pageIndex: 0, pageSize: 20 });
          setOpen(!open);
        }}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: `2px solid ${theme.palette.divider}`,
            pt: 2,
            px: 4,
            pb: 3,
            width: "80%",
            maxHeight: "90%",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              minHeight: "200px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: "0" }} id="parent-modal-title">
                {t("check-invite")}
              </h2>
              <Button
                onClick={() => {
                  setFilters(filterId, { pageIndex: 0, pageSize: 20 });
                  setOpen(!open);
                }}
                sx={{
                  borderRadius: "50%",
                  minWidth: "0",
                  padding: "0 3px",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  span: {
                    width: "0",
                  },
                }}
              >
                <CloseIcon fontSize="large" />
              </Button>
            </Box>
            {isLoading ? (
              <FormLoadingStateCst />
            ) : (
              <>
                <Alert
                  variant="outlined"
                  severity="info"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.text.tertiary,
                    mt: 2,
                    mb: -1,
                    "& .MuiAlert-icon": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {t("check-invite-alert")}
                </Alert>
                <Box
                  sx={{
                    mt: "25px",
                  }}
                >
                  <Table
                    id={PagesID["corporate.virtual-session-invitations"]}
                    data={dataTable?.output || []}
                    columns={filteredColumns || []}
                    configurationTopToolbar={actionsToolbar}
                    configurationActionForRow={[]}
                    totalPages={dataTable?.metadata?.total_elements}
                    enableRowActions={false}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SessionInvitationModalCst;
